import React from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { Instagram, Facebook, ShieldCheck, ArrowUp, Phone, Mail, MapPin } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'

export default function Footer() {
  const { language, t, linkTo } = useLanguage()
  const currentYear = new Date().getFullYear()
  const location = useLocation()
  const navigate = useNavigate()

  const handleNavClick = (e, href) => {
    e.preventDefault()

    if (href.startsWith('#')) {
      const isHome = location.pathname === '/' || location.pathname === '/en'
      if (isHome) {
        const element = document.querySelector(href)
        if (element) {
          const offset = 80 // height of navbar
          const bodyRect = document.body.getBoundingClientRect().top
          const elementRect = element.getBoundingClientRect().top
          const elementPosition = elementRect - bodyRect
          const offsetPosition = elementPosition - offset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
        }
      } else {
        navigate(linkTo(href))
      }
    } else {
      navigate(linkTo(href))
    }
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  return (
    <footer className="bg-[#241E1B] text-[#FAF8F5] pt-20 pb-10 overflow-hidden relative">
      {/* Background Image with elegant dark overlay */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <img 
          src="https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/99557a41-4297-4198-ac44-fb5c9d0f9512.jpg" 
          alt="Danish beach background" 
          className="w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#241E1B] via-[#241E1B]/95 to-[#241E1B]" />
      </div>

      {/* Subtle background waves/lines */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none z-0">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,100 Q300,50 600,100 T1200,100 T1800,100" fill="none" stroke="#FAF8F5" strokeWidth="2" />
          <path d="M0,150 Q300,100 600,150 T1200,150 T1800,150" fill="none" stroke="#FAF8F5" strokeWidth="2" />
        </svg>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-16 pb-16 border-b border-[#FAF8F5]/10">
          
          {/* Column 1 - Brand & Intro */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            <div className="flex items-center">
              <img 
                src="https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/9da1a640-3a0d-45d2-ae4d-fae068b574d0.png" 
                alt="Wecas ApS Logo" 
                className="h-26 sm:h-28 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity"
              />
            </div>
            <p className="text-sm text-[#FAF8F5]/70 leading-relaxed max-w-sm">
              {t('footer.about_desc')}
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#FAF8F5]/80 hover:text-[#a78458] transition-colors duration-300"
                aria-label="Instagram"
              >
                <Instagram className="h-6 w-6" />
              </a>
              <a
                href="https://www.facebook.com/share/18adkPfEas/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#FAF8F5]/80 hover:text-[#a78458] transition-colors duration-300"
                aria-label="Facebook"
              >
                <Facebook className="h-6 w-6" />
              </a>
            </div>
          </div>

          {/* Column 2 - Quick Links */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <h4 className="text-xs font-semibold tracking-[0.2em] text-[#FAF8F5]/50 uppercase">
              {t('footer.links')}
            </h4>
            <nav className="flex flex-col gap-3 text-sm text-[#FAF8F5]/80">
              <a href="#forside" onClick={(e) => handleNavClick(e, '#forside')} className="hover:text-[#a78458] transition-colors">{t('nav.home')}</a>
              <a href="#om-os" onClick={(e) => handleNavClick(e, '#om-os')} className="hover:text-[#a78458] transition-colors">{t('nav.about')}</a>
              <a href="#ydelser" onClick={(e) => handleNavClick(e, '#ydelser')} className="hover:text-[#a78458] transition-colors">{t('nav.services')}</a>
              <a href="#samarbejde" onClick={(e) => handleNavClick(e, '#samarbejde')} className="hover:text-[#a78458] transition-colors">{t('nav.collaboration')}</a>
              <a href="/kontakt" onClick={(e) => handleNavClick(e, '/kontakt')} className="hover:text-[#a78458] transition-colors">{t('nav.contact')}</a>
            </nav>
          </div>

          {/* Column 3 - Contact Info */}
          <div className="lg:col-span-3 flex flex-col gap-6">
            <h4 className="text-xs font-semibold tracking-[0.2em] text-[#FAF8F5]/50 uppercase">
              {t('footer.contact')}
            </h4>
            <div className="flex flex-col gap-4 text-sm text-[#FAF8F5]/80">
              <a href="tel:53843361" className="flex items-start gap-3 hover:text-[#a78458] transition-colors">
                <Phone className="h-4 w-4 text-[#FAF8F5] shrink-0 mt-0.5" />
                <span>53 84 33 61</span>
              </a>
              <a href="mailto:info@wecas.dk" className="flex items-start gap-3 hover:text-[#a78458] transition-colors">
                <Mail className="h-4 w-4 text-[#FAF8F5] shrink-0 mt-0.5" />
                <span>info@wecas.dk</span>
              </a>
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-[#FAF8F5] shrink-0 mt-0.5" />
                <span>Bredstrupvej 40,<br />7000 Fredericia</span>
              </div>
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-4 w-4 text-[#FAF8F5] shrink-0 mt-0.5" />
                <span>CVR: 45695425</span>
              </div>
            </div>
          </div>

          {/* Column 4 - Social Media QR Codes */}
          <div className="lg:col-span-3 flex flex-col gap-6">
            <h4 className="text-xs font-semibold tracking-[0.2em] text-[#FAF8F5]/50 uppercase">
              {language === 'da' ? 'Følg os på sociale medier' : 'Follow us on social media'}
            </h4>
            
            {/* Instagram QR Code */}
            <div className="flex flex-col gap-2">
              <span className="text-[11px] uppercase tracking-wider text-[#FAF8F5]/40 font-medium">Instagram</span>
              <a 
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-[#FAF8F5]/5 p-4 rounded-none border border-[#FAF8F5]/10 hover:bg-[#FAF8F5]/10 transition-colors duration-300 group"
              >
                {/* Minimalist, premium SVG QR Code representation */}
                <div className="h-16 w-16 bg-white p-1.5 rounded-none shrink-0 flex items-center justify-center shadow-inner">
                  <svg viewBox="0 0 100 100" className="w-full h-full text-[#2C302E]">
                    {/* Outer corners */}
                    <rect x="0" y="0" width="25" height="25" fill="currentColor" />
                    <rect x="2.5" y="2.5" width="20" height="20" fill="white" />
                    <rect x="7.5" y="7.5" width="10" height="10" fill="currentColor" />

                    <rect x="75" y="0" width="25" height="25" fill="currentColor" />
                    <rect x="77.5" y="2.5" width="20" height="20" fill="white" />
                    <rect x="82.5" y="7.5" width="10" height="10" fill="currentColor" />

                    <rect x="0" y="75" width="25" height="25" fill="currentColor" />
                    <rect x="2.5" y="77.5" width="20" height="20" fill="white" />
                    <rect x="7.5" y="82.5" width="10" height="10" fill="currentColor" />

                    {/* Random QR patterns */}
                    <rect x="35" y="5" width="10" height="15" fill="currentColor" />
                    <rect x="55" y="0" width="15" height="10" fill="currentColor" />
                    <rect x="45" y="20" width="15" height="10" fill="currentColor" />
                    <rect x="30" y="35" width="10" height="10" fill="currentColor" />
                    <rect x="50" y="30" width="20" height="15" fill="currentColor" />
                    <rect x="80" y="35" width="15" height="15" fill="currentColor" />
                    
                    <rect x="35" y="55" width="15" height="10" fill="currentColor" />
                    <rect x="55" y="65" width="10" height="20" fill="currentColor" />
                    <rect x="30" y="75" width="10" height="15" fill="currentColor" />
                    <rect x="75" y="55" width="20" height="10" fill="currentColor" />
                    <rect x="70" y="75" width="15" height="15" fill="currentColor" />

                    {/* Center dot */}
                    <circle cx="50" cy="50" r="6" fill="#a78458" />
                  </svg>
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-semibold text-[#FAF8F5] group-hover:text-[#a78458] transition-colors">{t('contact.scan_click')}</p>
                  <p className="text-[11px] text-[#FAF8F5]/60 leading-tight">{t('contact.insta_find')}</p>
                </div>
              </a>
            </div>

            {/* Facebook QR Code */}
            <div className="flex flex-col gap-2">
              <span className="text-[11px] uppercase tracking-wider text-[#FAF8F5]/40 font-medium">Facebook</span>
              <a 
                href="https://www.facebook.com/share/18adkPfEas/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-[#FAF8F5]/5 p-4 rounded-none border border-[#FAF8F5]/10 hover:bg-[#FAF8F5]/10 transition-colors duration-300 group"
              >
                {/* Minimalist, premium SVG QR Code representation */}
                <div className="h-16 w-16 bg-white p-1.5 rounded-none shrink-0 flex items-center justify-center shadow-inner">
                  <svg viewBox="0 0 100 100" className="w-full h-full text-[#2C302E]">
                    {/* Outer corners */}
                    <rect x="0" y="0" width="25" height="25" fill="currentColor" />
                    <rect x="2.5" y="2.5" width="20" height="20" fill="white" />
                    <rect x="7.5" y="7.5" width="10" height="10" fill="currentColor" />

                    <rect x="75" y="0" width="25" height="25" fill="currentColor" />
                    <rect x="77.5" y="2.5" width="20" height="20" fill="white" />
                    <rect x="82.5" y="7.5" width="10" height="10" fill="currentColor" />

                    <rect x="0" y="75" width="25" height="25" fill="currentColor" />
                    <rect x="2.5" y="77.5" width="20" height="20" fill="white" />
                    <rect x="7.5" y="82.5" width="10" height="10" fill="currentColor" />

                    {/* Random QR patterns (different layout for Facebook) */}
                    <rect x="30" y="10" width="15" height="10" fill="currentColor" />
                    <rect x="50" y="5" width="10" height="15" fill="currentColor" />
                    <rect x="40" y="25" width="15" height="10" fill="currentColor" />
                    <rect x="35" y="35" width="10" height="10" fill="currentColor" />
                    <rect x="55" y="30" width="15" height="20" fill="currentColor" />
                    <rect x="75" y="30" width="15" height="15" fill="currentColor" />
                    
                    <rect x="30" y="50" width="15" height="15" fill="currentColor" />
                    <rect x="50" y="60" width="15" height="15" fill="currentColor" />
                    <rect x="35" y="80" width="10" height="10" fill="currentColor" />
                    <rect x="70" y="50" width="20" height="10" fill="currentColor" />
                    <rect x="75" y="70" width="15" height="20" fill="currentColor" />

                    {/* Center dot */}
                    <circle cx="50" cy="50" r="6" fill="#a78458" />
                  </svg>
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-semibold text-[#FAF8F5] group-hover:text-[#a78458] transition-colors">{t('contact.scan_click')}</p>
                  <p className="text-[11px] text-[#FAF8F5]/60 leading-tight">{language === 'da' ? 'Besøg vores Facebook-side' : 'Visit our Facebook page'} <strong className="text-[#FAF8F5] group-hover:underline">Wecas ApS</strong></p>
                </div>
              </a>
            </div>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-10 text-xs text-[#FAF8F5]/50">
          <p>© {currentYear} Wecas ApS. {t('footer.rights')} CVR: 45695425</p>
          <div className="flex items-center gap-6">
            <a href="#privatlivspolitik" className="hover:text-[#FAF8F5] transition-colors">{language === 'da' ? 'Privatlivspolitik' : 'Privacy Policy'}</a>
            <a href="#cookiepolitik" className="hover:text-[#FAF8F5] transition-colors">{language === 'da' ? 'Cookiepolitik' : 'Cookie Policy'}</a>
            <button
              onClick={scrollToTop}
              className="h-10 w-10 rounded-none bg-[#FAF8F5]/5 flex items-center justify-center text-[#FAF8F5]/80 hover:bg-[#FAF8F5]/10 hover:text-[#FAF8F5] transition-all duration-300"
              aria-label={language === 'da' ? 'Rul til toppen' : 'Scroll to top'}
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  )
}
